import * as THREE from "three";
import {CBTextureType} from "./CBTypes";
import {CanvasContext} from "react-three-fiber";

export enum CBTextureLoadErrorReason {
    None='none',
    Aborted='aborted',
    Timeout='timeout',
    InvalidUrl='invalid-url'
}

const _canvas: { [label: string]: HTMLCanvasElement } = {};

export function getGlobalCanvas(label:string) : HTMLCanvasElement {
    if (!(label in _canvas)) {
        _canvas[label] = document.createElement("canvas")
    }
    return _canvas[label]
}

export function destroyAllCanvases() {
    for (let label in _canvas) {
        const canvas = _canvas[label];
        canvas.width = canvas.height = 1
    }
}

export function nearestPowerOf2(n:number) {
    return 1 << 31 - Math.clz32(n);
}

export class CBTextureLoader {

    textureLoader: THREE.TextureLoader;
    imageLoader: THREE.ImageLoader;
    image?: HTMLImageElement | null;

    constructor(public context:CanvasContext) {
        this.textureLoader = new THREE.TextureLoader();
        this.imageLoader = new THREE.ImageLoader(this.textureLoader.manager)
    }

    private _aborted = false;

    public abort() {
        if (this.image) {
            this.image.src = '';
            this._aborted = true
        }
    }

    public load(type:CBTextureType,
                url: string,
                canvas:HTMLCanvasElement|null,
                onLoad?: (texture: THREE.Texture, width:number, height:number) => void,
                onProgress?: (event: ProgressEvent) => void,
                onError?: (event: CBTextureLoadError) => void) {

        this.abort();

        if (this.textureLoader.crossOrigin) {
            this.imageLoader.setCrossOrigin(this.textureLoader.crossOrigin);
        }

        if (this.textureLoader.path) {
            this.imageLoader.setPath(this.textureLoader.path);
        }

        this.image = this.imageLoader.load(url, (image) => {

            let texture:THREE.Texture;
            if (canvas) {
                //to power of two here:
                canvas.width = Math.min(THREE.Math.ceilPowerOfTwo(image.width), this.context.gl.capabilities.maxTextureSize);
                canvas.height = Math.min(THREE.Math.ceilPowerOfTwo(image.height), this.context.gl.capabilities.maxTextureSize);
                const ctx = canvas.getContext("2d")!;
                ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
                texture = new THREE.Texture(canvas)

            } else {
                texture = new THREE.Texture(image)
            }
            this.image = null;

            // JPEGs can't have an alpha channel, so memory can be saved by storing them as RGB.
            const isJPEG = url.search(/\.jpe?g($|\?)/i) > 0 || url.search(/^data:image\/jpeg/) === 0;
            texture.format = isJPEG ? THREE.RGBFormat : THREE.RGBAFormat;
            texture.needsUpdate = true;

            if (onLoad !== undefined) {
                onLoad(texture, image.width, image.height);
                image.width = 1;
                image.height = 1;
            }
        }, (progress) => {
            if (onProgress) {
                onProgress(progress)
            }
        }, () => {
            if (!onError) return;

            let reason = CBTextureLoadErrorReason.None;

            if (this._aborted) {
                reason = CBTextureLoadErrorReason.Aborted
            } else if (url.length) {
                reason = CBTextureLoadErrorReason.InvalidUrl
            }

            onError(new CBTextureLoadError(type, url, reason))
        })
    };

    public dispose() {

    }
}

export class CBTextureLoadError extends Error {
    public reason: CBTextureLoadErrorReason = CBTextureLoadErrorReason.None;
    constructor(public type:CBTextureType, public path: string, _reason?: CBTextureLoadErrorReason, message?: string) {
        super(message);
        if (_reason) {
            this.reason = _reason
        }

        if (!message) {
            switch (this.reason) {
                case CBTextureLoadErrorReason.Aborted:
                    this.message = `Texture load ${path} aborted by user`;
                    break;
                case CBTextureLoadErrorReason.Timeout:
                    this.message = `Texture at ${path} timed out`;
                    break;
                case CBTextureLoadErrorReason.InvalidUrl:
                    this.message = `Texture not found at ${path}`;
                    break;
                default:
                    this.message = "Texture error"
            }
        }

        Object.setPrototypeOf(this, CBTextureLoadError.prototype);
    }
}